import {
  PlanWizardCategoryEnum,
  SOAP_PREFERENCE_SETTING_NAMES,
} from '@/libs/core/src/lib/constants/SOAPPreferences';
import { treatmentSubCategories } from '@/libs/core/src/lib/constants/treatmentKeys';
import { Checkbox } from '@chiroup/components/form/Checkbox';
import { debounce } from '@chiroup/components/form/debounce';
import { Textarea } from '@chiroup/components/form/Textarea';

import { TreatmentKeys } from '@chiroup/core/constants/treatmentKeys';
import { SpineParts, SpineSide } from '@chiroup/core/functions/bodyData';
import { Condition } from '@chiroup/core/types/Condition.type';
import {
  CarePlanComplaint,
  ComplaintStatuses,
  ImprovementOptions,
  ObjectiveNote,
  ObjectiveParts,
  SOANote,
  SOAPTemplates,
  Visit,
} from '@chiroup/core/types/PatientVisit.type';
import React, { use, useMemo, useState } from 'react';
import { getSpineDisplayName, getSpineSortWeight, JointRegions } from '../../../../../constants';
import { MeContext } from '../../../../../contexts/me.context';
import useConditionDetails from '../../../../../hooks/useConditionDetails';
import Modal from '../../../../common/Modal';
import useSettings from '../../../../report/hooks/useSettings';
import Improvement from './Improvement';
import Joints from './Joints';
import Myofascial from './Myofascial';
import Neurological from './Neurological';
import Orthopedic from './Orthopedic';
import RangeOfMotion from './RangeOfMotion';
import SOAPChangesWarning from './SOAPChangesWarning';
import { existsTreatmentSelection } from './UnifiedObjectiveStep';

const extraSpinalRegions: { [key: string]: string } = {
  'F/A': 'footAndAnkle',
  HEAD: 'head',
  RIBS: 'ribs',
  S: 'shoulder',
  K: 'knee',
  H: 'hipAndPelvis',
  E: 'elbow',
  'H/W': 'handAndWrist',
};

type SpinalRegions =
  | 'cervicalRegion'
  | 'thoracicRegion'
  | 'lumbarRegion'
  | 'sacralRegion'
  | 'pelvicRegion';

const spineToRegion: {
  [key: string]: number;
} = {
  C0: 1,
  C1: 1,
  C2: 1,
  C3: 1,
  C4: 1,
  C5: 1,
  C6: 1,
  C7: 1,
  T1: 2,
  T2: 2,
  T3: 2,
  T4: 2,
  T5: 2,
  T6: 2,
  T7: 2,
  T8: 2,
  T9: 2,
  T10: 2,
  T11: 2,
  T12: 2,
  '1st costovertebral': 2,
  '2nd costovertebral': 2,
  '3rd costovertebral': 2,
  '4th costovertebral': 2,
  '5th costovertebral': 2,
  '6th costovertebral': 2,
  '7th costovertebral': 2,
  '8th costovertebral': 2,
  '9th costovertebral': 2,
  '10th costovertebral': 2,
  '11th costovertebral': 2,
  '12th costovertebral': 2,
  L1: 3,
  L2: 3,
  L3: 3,
  L4: 3,
  L5: 3,
  S: 4,
  C: 4,
  PL: 5,
  PR: 5,
};

const regionKeys = [
  TreatmentKeys.CMT_SPINAL_5_REGIONS,
  TreatmentKeys.CMT_SPINAL_3_TO_4_REGIONS,
  TreatmentKeys.CMT_SPINAL_1_TO_2_REGIONS,
] as const;

const planObj: { [key: number]: string } = {
  0: '',
  1: TreatmentKeys.CMT_SPINAL_1_TO_2_REGIONS,
  2: TreatmentKeys.CMT_SPINAL_1_TO_2_REGIONS,
  3: TreatmentKeys.CMT_SPINAL_3_TO_4_REGIONS,
  4: TreatmentKeys.CMT_SPINAL_3_TO_4_REGIONS,
  5: TreatmentKeys.CMT_SPINAL_5_REGIONS,
};
export const cmtServiceCodes: { [key: string]: string } = {
  [TreatmentKeys.CMT_SPINAL_1_TO_2_REGIONS]: '98940',
  [TreatmentKeys.CMT_SPINAL_3_TO_4_REGIONS]: '98941',
  [TreatmentKeys.CMT_SPINAL_5_REGIONS]: '98942',
};

export const getCopyOfDetail = (
  cmt: Record<
    string,
    {
      detail?: Record<
        string,
        {
          region: string;
          notes?: string;
          type?: string[];
        }
      >;
    }
  >
) => {
  const selectedKey = regionKeys.find(key => cmt[key]?.detail);
  const rawDetail = selectedKey ? cmt[selectedKey].detail : {};

  return JSON.parse(JSON.stringify(rawDetail || {}));
};

export const getNumberOfSpineRegionsAndDetailNotes = (
  soanote: {
    [key: string]: SOANote;
  },
  complaints: CarePlanComplaint[],
  planTypesInDetailNote: Record<string, string[]>
) => {
  const activeComplaints = complaints?.reduce(
    (obj, comp) => {
      if (
        comp.status !== ComplaintStatuses.INACTIVE &&
        comp.status !== ComplaintStatuses.PAUSED &&
        !comp?.skipped
      ) {
        obj[comp.id || ''] = comp;
      }

      return obj;
    },
    {} as Record<string, CarePlanComplaint>
  );
  //check to make sure we are only analyzing active complaints
  const noteWithOnlyActiveComplaints = Object.entries(soanote).reduce(
    (acc, [key, note]) => {
      if (activeComplaints?.[key]) {
        acc[key] = note;
      }

      return acc;
    },
    {} as { [key: string]: SOANote }
  );
  const notes = Object.values(noteWithOnlyActiveComplaints);
  const foundRegions: {
    [key: number]: boolean;
  } = {
    1: false,
    2: false,
    3: false,
    4: false,
    5: false,
  };

  const allSpineRegionsSelected = notes?.reduce((arr: string[], note) => {
    const spineRegions = Object.entries(note?.objective?.Joints?.detail || {}).find(([key, _]) =>
      key.includes('SPINE')
    );
    const ribRegions = Object.entries(note?.objective?.Joints?.detail || {}).find(([key, _]) =>
      key.includes('RIBS')
    );
    const costoVertebralRegions = Object.keys(ribRegions?.[1] || {}).filter(val =>
      val.includes('costovertebral')
    );

    const spineRegionsArr = Object.entries(spineRegions?.[1] || {}).reduce(
      (arr: string[], [key, value]) => {
        // Ignore metadata and non-spine keys
        const isMeta = key === 'LLI' || key === 'complaintIds' || key === 'addedManually';
        const isKnownSpineKey = Object.prototype.hasOwnProperty.call(spineToRegion, key);

        if (!isMeta && isKnownSpineKey && value !== 'NONE') {
          arr.push(key);
        }

        return arr;
      },
      []
    );

    return arr.concat(spineRegionsArr, costoVertebralRegions);
  }, []);
  const uniqueSpineSelections = [...new Set(allSpineRegionsSelected)];
  const generatedNotes = uniqueSpineSelections?.reduce(
    (
      obj: {
        cervicalRegion: string[];
        thoracicRegion: string[];
        lumbarRegion: string[];
        sacralRegion: string[];
        pelvicRegion: string[];
      },
      spineSelection
    ) => {
      const spineRegion = spineToRegion[spineSelection];

      switch (spineRegion) {
        case 1:
          obj.cervicalRegion.push(spineSelection);
          break;
        case 2:
          obj.thoracicRegion.push(spineSelection);
          break;
        case 3:
          obj.lumbarRegion.push(spineSelection);
          break;
        case 4:
          obj.sacralRegion.push(spineSelection);
          break;
        case 5:
          obj.pelvicRegion.push(spineSelection);
          break;
        default:
          break;
      }

      return obj;
    },
    {
      cervicalRegion: [],
      thoracicRegion: [],
      lumbarRegion: [],
      sacralRegion: [],
      pelvicRegion: [],
    }
  );

  const regionCount = uniqueSpineSelections?.reduce((count: number, spineSelection: string) => {
    const spineRegion = spineToRegion[spineSelection];

    if (!foundRegions[spineRegion]) {
      foundRegions[spineRegion] = true;
      count++;
    }

    return count;
  }, 0);

  const detailRegions = [
    { cervicalRegion: foundRegions[1] },
    { thoracicRegion: foundRegions[2] },
    { lumbarRegion: foundRegions[3] },
    { sacralRegion: foundRegions[4] },
    { pelvicRegion: foundRegions[5] },
  ];

  let index = 1;

  const detailNotes = detailRegions.reduce(
    (
      obj: {
        [key: string]: { region: string; notes: string; type: string[] };
      },
      regionObj
    ) => {
      const key = Object.keys(regionObj)[0];
      const value = regionObj[key as keyof typeof regionObj];

      if (value) {
        obj[Number(index)] = {
          region: key,
          notes: generatedNotes?.[key as keyof typeof generatedNotes]
            ?.sort((a: string, b: string) => getSpineSortWeight(a) - getSpineSortWeight(b))
            ?.map(getSpineDisplayName)
            ?.join(', '),
          type: planTypesInDetailNote?.[key],
        };
        index++;
      }

      return obj;
    },
    {}
  );

  return {
    keyCount: planObj[regionCount],
    detailNotes,
  };
};

/**
 * Calculate what cmtExtraspinal configuration should be based on the note data
 * This function determines which extraspinal regions should be included
 * and creates the appropriate cmtExtraspinal1+ configuration
 *
 * @param noteData The note data containing all complaints and their objective findings
 * @param complaints Array of complaints to check which ones are active
 * @returns The cmtExtraspinal object if needed, or false if not needed
 */
export const calculateCmtExtraspinal = (
  noteData: Record<string, any> = {},
  complaints: CarePlanComplaint[] = []
): Record<string, any> | false => {
  // Step 1: Find all regions across all active complaints
  const activeComplaints = complaints?.reduce(
    (obj, comp) => {
      if (
        comp.status !== ComplaintStatuses.INACTIVE &&
        comp.status !== ComplaintStatuses.PAUSED &&
        !comp?.skipped
      ) {
        obj[comp.id || ''] = comp;
      }

      return obj;
    },
    {} as Record<string, CarePlanComplaint>
  );

  // Filter notes to only include active complaints
  const noteWithOnlyActiveComplaints = Object.entries(noteData).reduce(
    (acc, [key, note]) => {
      if (activeComplaints?.[key]) {
        acc[key] = note;
      }

      return acc;
    },
    {} as { [key: string]: SOANote }
  );

  // Build a map of all joint regions found in active complaints
  // (but only include regions that have actual joint selections, not just metadata)
  const allRegionsHash: { [key: string]: boolean } = {};

  for (const complaintId in noteWithOnlyActiveComplaints) {
    const jointDetail = noteWithOnlyActiveComplaints[complaintId]?.objective?.Joints?.detail || {};

    Object.keys(jointDetail).forEach(region => {
      const regionData = jointDetail[region];

      // check for actual joint selections, not just metadata
      const hasActualJoints = Object.keys(regionData).some(
        key => key !== 'complaintIds' && key !== 'addedManually'
      );

      if (hasActualJoints) {
        allRegionsHash[region] = true;
      }
    });
  }

  // Step 2: Check for costoSternal ribs (special case)
  let costoSternalRibs = false;

  for (const complaintId in noteWithOnlyActiveComplaints) {
    const ribsInfo =
      noteWithOnlyActiveComplaints[complaintId]?.objective?.Joints?.detail?.['RIBS'] || {};

    if (Object.keys(ribsInfo).some(key => key.includes('costosternal'))) {
      costoSternalRibs = true;
      break;
    }
  }

  // Step 3: Determine if we need extraSpinal treatment
  const keepExtraSpinalInPlan = Object.keys(allRegionsHash).some(
    key => key !== 'SPINE' && key !== 'RIBS'
  );
  const keepExtraSpinal = keepExtraSpinalInPlan || costoSternalRibs;

  // Step 4: Return false if no extraspinal needed
  if (!keepExtraSpinal) {
    return false;
  }

  // Step 5: Create the extraspinal configuration
  const regionsToInclude: string[] = [];

  // Add non-spine regions except RIBS (handled specially)
  for (const region in allRegionsHash) {
    if (region !== 'SPINE' && region !== 'RIBS' && extraSpinalRegions[region]) {
      const mappedRegion = extraSpinalRegions[region];

      // Avoid duplicates in the mapped regions
      if (!regionsToInclude.includes(mappedRegion)) {
        regionsToInclude.push(mappedRegion);
      }
    }
  }

  // Handle RIBS specially if it contains costosternal findings
  if (costoSternalRibs && !regionsToInclude.includes(extraSpinalRegions['RIBS'])) {
    regionsToInclude.push(extraSpinalRegions['RIBS']);
  }

  // Limit to max 5 regions
  const finalRegions = regionsToInclude.slice(0, 5);

  // If no regions after all filtering, return false
  if (finalRegions.length === 0) {
    return false;
  }

  // Create the detail object
  const detail: Record<string, { region: string }> = {};
  finalRegions.forEach((region, index) => {
    detail[(index + 1).toString()] = { region };
  });

  // Return just the cmtExtraspinal object
  return {
    cmtExtraspinal: {
      'cmtExtraspinal1+': {
        units: 1,
        services: ['98943'],
        detail: detail,
      },
    },
  };
};

type Props = {
  value?: ObjectiveNote;
  setVisitForm: React.Dispatch<React.SetStateAction<Visit>>;
  complaintConditions: number[];
  signed?: boolean;
  complaintId: string;
  visitForm?: Visit;
  isSaving?: boolean;
  readOnly?: boolean;
};

const ObjectiveStep: React.FC<Props> = ({
  value,
  setVisitForm,
  complaintConditions,
  signed,
  complaintId,
  visitForm,
  isSaving = false,
  readOnly,
}) => {
  const { me } = use(MeContext);
  const [rangeOfMotionIsOpen, setRangeOfMotionIsOpen] = useState(false);
  const [myofascialIsOpen, setMyofascialIsOpen] = useState(false);
  const [jointsIsOpen, setJointsIsOpen] = useState(false);
  const [neurologicalIsOpen, setNeurologicalIsOpen] = useState(false);
  const [orthopedicIsOpen, setOrthopedicIsOpen] = useState(false);
  const [partOpen, setPartOpen] = useState<ObjectiveParts>(ObjectiveParts.Joints);
  const [localTextareaValue, setLocalTextareaValue] = useState(value?.extraNotes || '');

  // debounce the visitform update
  const debouncedSetVisitForm = useMemo(
    () =>
      debounce((val: string) => {
        setVisitForm(prev => {
          const newVal = {
            ...prev,
            soapCustomEditWarning: {
              ...prev?.soapCustomEditWarning,
              objective: false,
            },
            note: prev?.note ? prev?.note : {},
          };
          const newNote = newVal.note?.[complaintId] || {};
          newNote.objective = newNote.objective || {};
          newNote.objective.extraNotes = val;
          newNote.objectiveCarryForward = newNote.objectiveCarryForward || false;
          newVal.note[complaintId] = newNote;

          return newVal;
        });
      }), // default debounce time is 300ms
    [complaintId, setVisitForm]
  );

  // update both local textarea state and the visitform state
  const handleObjectiveChange = (val: string) => {
    setLocalTextareaValue(val); // value for controlled textfield
    debouncedSetVisitForm(val); // visitform state
  };

  const { data: conditionsDetails, isFetching } = useConditionDetails({
    ids: complaintConditions,
    refetchOnMount: false,
  });
  const { data: romData } = useSettings({ type: SOAP_PREFERENCE_SETTING_NAMES.SOAP_PREFERENCES });
  const romSliderStep = useMemo(() => {
    const romSlider = romData?.find(
      item =>
        item?.type === SOAP_PREFERENCE_SETTING_NAMES.SOAP_PREFERENCES &&
        item?.name === SOAP_PREFERENCE_SETTING_NAMES.OBJECTIVE_ROM
    )?.jsonValue;

    return romSlider ? Number(romSlider) : 5;
  }, [romData]);

  const conditionsRetrieved = useMemo(() => {
    const conditionsArray = conditionsDetails?.data || [];

    return conditionsArray.reduce(
      (obj: { [key: number]: Partial<Condition> }, cond: Partial<Condition>) => {
        if (cond.ID) {
          obj[cond.ID] = cond;
        }

        return obj;
      },
      {}
    );
  }, [conditionsDetails?.data]);

  const onChangeImprovement = (key: ObjectiveParts) => {
    return (val: ImprovementOptions | null) => {
      if (signed || isSaving || readOnly) return;
      setVisitForm(prev => {
        const newVal = {
          ...prev,
          soapCustomEditWarning: {
            ...prev?.soapCustomEditWarning,
            objective: false,
          },
          note: prev?.note ? prev?.note : {},
        };
        const newNote = newVal.note?.[complaintId] || {};
        const newObjective = { ...(newNote.objective || {}) };
        const newPart = { ...(newObjective[key] || {}) };
        const previousValue = newPart.value;

        if (val && previousValue !== val) {
          newPart.value = val;
        } else if (!val && previousValue) {
          delete newPart.value;
        }

        newObjective[key] = newPart;
        newNote.objective = newObjective;
        newVal.note[complaintId] = newNote;

        return newVal;
      });
    };
  };

  const onChangeDetail = (key: ObjectiveParts) => {
    return (
      region: string,
      prop: string,
      val: {
        value?: number;
        pain?: boolean;
        side?: 'Right' | 'Left' | 'Both' | 'SpinalArea' | 'NONE';
      }
    ) => {
      if (signed || isSaving || readOnly) return;
      setVisitForm(prev => {
        if (Array.isArray(prev.note)) {
          prev.note = {};
        }

        const newVal = JSON.parse(
          JSON.stringify({
            ...prev,
            note: prev?.note || {},
            plan: prev?.plan || {},
          })
        );
        newVal.soapCustomEditWarning = {
          ...prev?.soapCustomEditWarning,
          objective: false,
        };
        const newNote = newVal.note?.[complaintId] || {};
        newNote.objective = newNote.objective || {};

        if (key === 'RangeOfMotion') {
          const romData = newNote.objective[key] || {};
          romData.detail = romData?.detail || {};
          romData.detail[region] = { ...romData.detail[region], touched: true };
          romData.detail[region][prop] = romData.detail[region][prop] || {};

          if (region === 'C' || region === 'L' || region === 'T') {
            if (val.value === undefined && !val.pain) {
              delete romData.detail[region][prop];
            } else {
              romData.detail[region][prop]['SpinalArea'] =
                romData.detail[region][prop]['SpinalArea'] || {};
              romData.detail[region][prop]['SpinalArea'] = {
                pain: val?.pain,
                value: val?.value,
              };
            }
          } else {
            romData.detail[region][prop][val?.side || 'Both'] =
              romData.detail[region][prop][val?.side || 'Both'] || {};
            romData.detail[region][prop][val?.side || 'Both'] = {
              pain: val?.pain,
              value: val?.value,
            };
          }

          //We have to delete stuff off otherwise the note will show headers for undefined values.
          if (val.value === undefined && !val.pain && val.side) {
            delete romData.detail[region][prop];
          }

          if (
            Object.keys(romData.detail[region]).length === 1 &&
            Object.keys(romData.detail[region])[0] === 'touched'
          ) {
            delete romData.detail[region];

            if (!Object.keys(romData.detail).length) {
              delete romData.detail;
            }
          }
          // Done deleting stuff off.

          newNote.objective[key] = romData;
          newVal.note[complaintId] = newNote;
        } else if (key === 'Joints') {
          newNote.objective[key] = newNote.objective[key] || {};
          newNote.objective[key].detail = newNote.objective[key]?.detail || {};

          if (region === 'SPINE') {
            if (prop === 'LLI') {
              if (!val?.side) {
                delete newNote.objective[key].detail[region][prop];

                if (!Object.keys(newNote.objective[key].detail[region]).length) {
                  delete newNote.objective[key].detail[region];
                }
              } else {
                newNote.objective[key].detail[region] = {
                  ...newNote.objective[key].detail[region],
                  [prop]: { side: val?.side, value: val?.pain },
                };
              }
            } else if (val?.side === 'NONE') {
              if (newNote?.objective?.[key]?.detail?.[region]?.[prop]) {
                delete newNote.objective[key].detail[region][prop];
              }

              if (!Object.keys(newNote?.objective?.[key]?.detail?.[region] || {}).length) {
                delete newNote.objective[key].detail[region];
              }
            } else {
              newNote.objective[key].detail[region] = {
                ...newNote.objective[key].detail[region],
                [prop]: val?.side,
              };
            }
          } else {
            if (newNote.objective[key]?.detail?.[region]?.[prop]?.[val.toString()] === true) {
              delete newNote.objective[key].detail?.[region]?.[prop];

              if (!Object.keys(newNote.objective[key].detail[region]).length) {
                delete newNote.objective[key].detail[region];
              }
            } else {
              newNote.objective[key].detail[region] = {
                ...newNote.objective[key].detail[region],
                [prop]: {
                  ...newNote.objective[key].detail[region]?.[prop],
                  [val.toString()]: true,
                },
              };
            }
          }

          // newNote.objective[key] = jointData;
          newVal.note[complaintId] = newNote;

          const planWizardCategoryAndTreatmentVisible =
            (prev?.planWizardCategory &&
              prev?.planWizardCategory?.find(cat => cat.name === PlanWizardCategoryEnum.Treatments)
                ?.visible) ||
            !prev?.planWizardCategory;

          //logic below deals with adding and removing regions from the plan and the detail associated.
          if (
            planWizardCategoryAndTreatmentVisible &&
            ((region === 'RIBS' && prop?.includes('costovertebral')) || region === 'SPINE')
          ) {
            const copyOfDetail = getCopyOfDetail(newVal?.plan?.treatments?.cmt ?? {});

            const planTypesInDetailNote = Object.keys(copyOfDetail || {})?.reduce(
              (obj, key) => {
                obj[copyOfDetail[key]?.region] = copyOfDetail[key]?.type || [];

                return obj;
              },
              {} as Record<string, string[]>
            );

            const spineRegionsAndDetailNotes = getNumberOfSpineRegionsAndDetailNotes(
              newVal?.note,
              newVal?.encounterInfo?.complaints,
              planTypesInDetailNote
            );

            const existsSelection = existsTreatmentSelection(
              prev?.planWizardCategory,
              selection => String(selection.ID) === (spineRegionsAndDetailNotes.keyCount as string)
            );

            if (spineRegionsAndDetailNotes?.keyCount && existsSelection) {
              newVal.plan = {
                ...newVal?.plan,
                treatments: {
                  ...newVal?.plan?.treatments,
                  cmt: {
                    [spineRegionsAndDetailNotes.keyCount]: {
                      units: 1,
                      services: [cmtServiceCodes?.[spineRegionsAndDetailNotes.keyCount]],
                      unitServices: null,
                      detail: spineRegionsAndDetailNotes?.detailNotes,
                    },
                  },
                },
              };
            } else {
              delete newVal.plan?.treatments?.cmt;

              if (!Object.keys(newVal.plan?.treatments || {}).length) {
                delete newVal.plan?.treatments;
              }
            }
          } else if (planWizardCategoryAndTreatmentVisible) {
            // Calculating extraspinal for the plan
            const cmtExtraspinalResult = calculateCmtExtraspinal(
              newVal?.note,
              newVal?.encounterInfo?.complaints
            );

            if (!cmtExtraspinalResult) {
              delete newVal?.plan?.treatments?.cmtExtraspinal;

              if (!Object.keys(newVal?.plan?.treatments || {}).length) {
                delete newVal?.plan?.treatments;
              }
            } else {
              const existsSelectionExtraspinal = existsTreatmentSelection(
                prev?.planWizardCategory,
                selection => selection.subCategory === treatmentSubCategories.CMT_EXTRASPINAL
              );

              if (!cmtExtraspinalResult || !cmtExtraspinalResult.cmtExtraspinal) {
                delete newVal?.plan?.treatments?.cmtExtraspinal;

                if (!Object.keys(newVal?.plan?.treatments || {}).length) {
                  delete newVal?.plan?.treatments;
                }
              }

              if (cmtExtraspinalResult && existsSelectionExtraspinal) {
                newVal.plan = newVal.plan || {};
                newVal.plan.treatments = newVal.plan.treatments || {};
                newVal.plan.treatments = {
                  ...newVal.plan.treatments,
                  ...cmtExtraspinalResult,
                };
              }
            }
          }
        } else if (key === 'Myofascial') {
          const myofascialData = newNote.objective[key] || {};
          myofascialData.detail = val.value;
          newNote.objective[key] = myofascialData;
          newVal.note[complaintId] = newNote;
        } else if (key === 'Neurological') {
          const neurologicalData = newNote.objective[key] || {};
          neurologicalData.detail = val.value;
          newNote.objective[key] = neurologicalData;
          newVal.note[complaintId] = newNote;
        } else if (key === 'Orthopedic') {
          const orthoData = newNote.objective[key] || {};
          orthoData.detail = val.value;
          newNote.objective[key] = orthoData;
          newVal.note[complaintId] = newNote;
        }

        return newVal;
      });
    };
  };

  // V1-specific spine callback that formats data according to V1 ObjectiveStep expectations
  const onChangeSpineV1 = (part: SpineParts, value?: SpineSide, amount?: number) => {
    if (signed || isSaving || readOnly) return;

    setVisitForm(prev => {
      const newVal = { ...prev };
      newVal.note = newVal.note || {};
      newVal.note[complaintId] = newVal.note[complaintId] || {};
      newVal.note[complaintId].objective = newVal.note[complaintId].objective || {};
      newVal.note[complaintId].objective[ObjectiveParts.Joints] =
        newVal.note[complaintId].objective[ObjectiveParts.Joints] || {};
      newVal.note[complaintId].objective[ObjectiveParts.Joints].detail =
        newVal.note[complaintId].objective[ObjectiveParts.Joints].detail || {};
      newVal.note[complaintId].objective[ObjectiveParts.Joints].detail[JointRegions.SPINE] =
        newVal.note[complaintId].objective[ObjectiveParts.Joints].detail[JointRegions.SPINE] || {};

      // Handle LLI special case (expects {side, value} object)
      if (part === 'LLI') {
        if (value === SpineSide.NONE || !value) {
          delete newVal.note[complaintId].objective[ObjectiveParts.Joints].detail[
            JointRegions.SPINE
          ][part];
        } else {
          newVal.note[complaintId].objective[ObjectiveParts.Joints].detail[JointRegions.SPINE][
            part
          ] = {
            side: value,
            value: amount || 0,
          };
        }
      } else {
        // Handle regular spine parts (expects SpineSide value directly)
        if (value === SpineSide.NONE || !value) {
          delete newVal.note[complaintId].objective[ObjectiveParts.Joints].detail[
            JointRegions.SPINE
          ][part];
        } else {
          newVal.note[complaintId].objective[ObjectiveParts.Joints].detail[JointRegions.SPINE][
            part
          ] = value;
        }
      }

      const copyOfDetail = getCopyOfDetail(newVal?.plan?.treatments?.cmt ?? {});

      const planTypesInDetailNote = Object.keys(copyOfDetail || {})?.reduce(
        (obj, key) => {
          obj[copyOfDetail[key]?.region] = copyOfDetail[key]?.type || [];

          return obj;
        },
        {} as Record<string, string[]>
      );

      const spineRegionsAndDetailNotes = getNumberOfSpineRegionsAndDetailNotes(
        newVal?.note,
        newVal?.encounterInfo?.complaints || [],
        planTypesInDetailNote
      );

      if (spineRegionsAndDetailNotes?.keyCount) {
        newVal.plan = {
          ...newVal?.plan,
          treatments: {
            ...newVal?.plan?.treatments,
            cmt: {
              [spineRegionsAndDetailNotes.keyCount]: {
                units: 1,
                services: [cmtServiceCodes?.[spineRegionsAndDetailNotes.keyCount]],
                unitServices: null,
                detail: spineRegionsAndDetailNotes?.detailNotes,
              },
            },
          },
        };
      } else {
        delete newVal.plan?.treatments?.cmt;

        if (!Object.keys(newVal.plan?.treatments || {}).length) {
          delete newVal.plan?.treatments;
        }
      }

      return newVal;
    });
  };

  const onRemoveRegion = (key: ObjectiveParts) => {
    return (region: string) => {
      if (signed || isSaving || readOnly) return;
      setVisitForm(prev => {
        const newVal = JSON.parse(JSON.stringify({ ...prev, note: prev?.note ? prev?.note : {} }));
        newVal.soapCustomEditWarning = {
          ...prev?.soapCustomEditWarning,
          objective: false,
        };

        //the next if else logic removes the region from the plan..
        //removing regions happens in onchangeDetail as well.  may be able to consolidate into a single function.
        if (region === 'RIBS' || region === 'SPINE') {
          //for ribs costoVertebral falls into cmt while costosternal falls into cmtExtraspinal.
          const copyOfSpinalDetail: any = newVal?.plan?.treatments?.cmt?.cmtSpinal5Regions?.detail
            ? {
                ...newVal?.plan?.treatments?.cmt?.['cmtSpinal5Regions']?.detail,
              }
            : newVal?.plan?.treatments?.cmt?.['cmtSpinal3-4Regions']?.detail
              ? {
                  ...newVal?.plan?.treatments?.cmt?.['cmtSpinal3-4Regions']?.detail,
                }
              : newVal?.plan?.treatments?.cmt?.['cmtSpinal1-2Regions']?.detail
                ? {
                    ...newVal?.plan?.treatments?.cmt?.['cmtSpinal1-2Regions']?.detail,
                  }
                : {};

          if (region === 'RIBS') {
            const indexToDeleteExtra = Object.entries(
              newVal?.plan?.treatments?.cmtExtraspinal?.['cmtExtraspinal1+']?.detail || {}
            ).filter(([key, val]: any) => val.region === 'ribs')?.[0]?.[0];

            delete newVal?.plan?.treatments?.cmtExtraspinal?.['cmtExtraspinal1+']?.detail?.[
              indexToDeleteExtra
            ];

            if (
              !Object.keys(
                newVal.plan?.treatments?.cmtExtraspinal?.['cmtExtraspinal1+']?.detail || {}
              )?.length
            ) {
              delete newVal.plan?.treatments?.cmtExtraspinal;
            }

            const thoracicSpineRegionsNotRibs = Object.entries(
              newVal?.note[complaintId]?.objective?.Joints?.detail?.SPINE || {}
            ).some(([key, _]) => key.includes('T'));

            if (!thoracicSpineRegionsNotRibs) {
              const indexToDelete = Object.entries(copyOfSpinalDetail || {}).filter(
                ([key, val]: any) => val.region === 'thoracicRegion'
              )?.[0]?.[0];
              delete copyOfSpinalDetail?.[indexToDelete];

              if (!copyOfSpinalDetail?.length) {
                delete newVal?.plan?.treatments?.cmt;
              }
            }
          } else {
            const anyCostalVertebralRibs = Object.keys(
              newVal?.note?.[complaintId]?.objective?.Joints?.detail?.RIBS || {}
            ).some(key => key.includes('costovertebral'));

            delete newVal?.plan?.treatments?.cmt?.['cmtSpinal5Regions'];
            delete newVal?.plan?.treatments?.cmt?.['cmtSpinal3-4Regions'];
            delete newVal?.plan?.treatments?.cmt?.['cmtSpinal1-2Regions'];

            if (anyCostalVertebralRibs) {
              newVal.plan.treatments = {
                ...newVal?.plan?.treatments,
                cmt: {
                  'cmtSpinal1-2Regions': {
                    detail: {
                      1: {
                        region: 'thoracicRegion',
                        notes: '',
                      },
                    },
                    units: 0,
                  },
                },
              };
            }
          }
        } else {
          const regionIndexToDelete = Object.entries(
            newVal?.plan?.treatments?.cmtExtraspinal?.['cmtExtraspinal1+']?.detail || {}
          ).filter(([key, val]: any) => val.region === extraSpinalRegions[region])?.[0]?.[0];

          delete newVal.plan?.treatments?.cmtExtraspinal?.['cmtExtraspinal1+']?.detail?.[
            regionIndexToDelete
          ];

          if (
            !Object.keys(
              newVal?.plan?.treatments?.cmtExtraspinal?.['cmtExtraspinal1+']?.detail || {}
            ).length
          ) {
            delete newVal?.plan?.treatments?.cmtExtraspinal;
          }
        }

        const newNote = newVal.note?.[complaintId] || {};
        newNote.objective = newNote.objective || {};
        const romData = newNote.objective[key] || {};
        romData.detail = romData?.detail || {};
        delete romData.detail[region];
        newNote.objective[key] = romData;
        newVal.note[complaintId] = newNote;

        return newVal;
      });
    };
  };

  return (
    <>
      <div className="flex flex-col w-full gap-6 p-3">
        {' '}
        {!visitForm?.signed &&
          visitForm?.soapCustomEditWarning?.objective &&
          visitForm?.soapTemplate !== SOAPTemplates.Blank && <SOAPChangesWarning />}
        <div className="flex flex-row w-full gap-4">
          <div className="w-full xl:w-96">
            <div className="flex flex-col text-gray-900 bg-white border-b border-gray-300 divide-y divide-gray-300 rounded-lg shadow-sm dark:bg-dark-gray-800 print:ring-0 print:shadow-none ring-1 ring-gray-300 dark:ring-dark-gray-600 dark:divide-dark-gray-600 dark:text-dark-gray-50 dark:border-dark-gray-600">
              <Improvement
                title="Joints"
                detail={() => setJointsIsOpen(true)}
                isSaving={isSaving}
                detailDesktop={() => setPartOpen(ObjectiveParts.Joints)}
                detailSelected={partOpen === ObjectiveParts.Joints}
                onChange={onChangeImprovement(ObjectiveParts.Joints)}
                value={visitForm?.note?.[complaintId]?.objective?.[ObjectiveParts.Joints]?.value}
                showOptions={!!visitForm?.note?.[complaintId]?.objective?.Joints?.showGlobal}
                top
                largeText
                disabled={signed || readOnly}
              />
              <Improvement
                title="Range of Motion"
                detail={() => setRangeOfMotionIsOpen(true)}
                isSaving={isSaving}
                detailDesktop={() => setPartOpen(ObjectiveParts.RangeOfMotion)}
                detailSelected={partOpen === ObjectiveParts.RangeOfMotion}
                onChange={onChangeImprovement(ObjectiveParts.RangeOfMotion)}
                value={
                  visitForm?.note?.[complaintId]?.objective?.[ObjectiveParts.RangeOfMotion]?.value
                }
                showOptions={!!visitForm?.note?.[complaintId]?.objective?.RangeOfMotion?.showGlobal}
                largeText
                disabled={signed || readOnly}
              />
              <Improvement
                title="Myofascial"
                detail={() => setMyofascialIsOpen(true)}
                isSaving={isSaving}
                detailDesktop={() => setPartOpen(ObjectiveParts.Myofascial)}
                detailSelected={partOpen === ObjectiveParts.Myofascial}
                onChange={onChangeImprovement(ObjectiveParts.Myofascial)}
                value={
                  visitForm?.note?.[complaintId]?.objective?.[ObjectiveParts.Myofascial]?.value
                }
                showOptions={!!visitForm?.note?.[complaintId]?.objective?.Myofascial?.showGlobal}
                largeText
                disabled={signed || readOnly}
              />
              <Improvement
                title="Orthopedic"
                detail={() => setOrthopedicIsOpen(true)}
                isSaving={isSaving}
                detailDesktop={() => setPartOpen(ObjectiveParts.Orthopedic)}
                detailSelected={partOpen === ObjectiveParts.Orthopedic}
                onChange={onChangeImprovement(ObjectiveParts.Orthopedic)}
                value={
                  visitForm?.note?.[complaintId]?.objective?.[ObjectiveParts.Orthopedic]?.value
                }
                showOptions={!!visitForm?.note?.[complaintId]?.objective?.Orthopedic?.showGlobal}
                largeText
                disabled={signed || readOnly}
              />
              <Improvement
                title="Neurologic"
                detail={() => setNeurologicalIsOpen(true)}
                isSaving={isSaving}
                detailDesktop={() => setPartOpen(ObjectiveParts.Neurological)}
                detailSelected={partOpen === ObjectiveParts.Neurological}
                onChange={onChangeImprovement(ObjectiveParts.Neurological)}
                value={
                  visitForm?.note?.[complaintId]?.objective?.[ObjectiveParts.Neurological]?.value
                }
                showOptions={!!visitForm?.note?.[complaintId]?.objective?.Neurological?.showGlobal}
                bottom
                largeText
                disabled={signed || readOnly}
              />
            </div>
          </div>
          {partOpen === ObjectiveParts.Joints ? (
            <div className="hidden xl:contents w-full bg-white dark:bg-dark-gray-800">
              <div className="flex flex-col w-full mx-auto text-gray-900 bg-white border-b border-gray-300 divide-y divide-gray-300 rounded-lg shadow-sm print:ring-0 print:shadow-none ring-1 ring-gray-300 dark:ring-dark-gray-700 dark:divide-dark-gray-600 dark:border-dark-gray-600 dark:bg-dark-gray-800">
                <Joints
                  value={value?.[ObjectiveParts.Joints]?.detail || []}
                  onChange={onChangeDetail(ObjectiveParts.Joints)}
                  onChangeSpine={onChangeSpineV1}
                  onRemoveRegion={onRemoveRegion(ObjectiveParts.Joints)}
                  isFetching={isFetching}
                  signed={signed || readOnly}
                />
              </div>
            </div>
          ) : (
            <div className="hidden xl:contents w-full bg-white dark:bg-dark-gray-800">
              <div className="flex flex-col w-full mx-auto text-gray-900 bg-white border-b border-gray-300 divide-y divide-gray-300 rounded-lg shadow-sm print:ring-0 print:shadow-none ring-1 ring-gray-300 dark:ring-dark-gray-700 dark:divide-dark-gray-600 dark:border-dark-gray-600 dark:bg-dark-gray-800">
                {partOpen === ObjectiveParts.RangeOfMotion && (
                  <div className="p-4">
                    <RangeOfMotion
                      value={value?.[ObjectiveParts.RangeOfMotion]?.detail}
                      onChange={onChangeDetail(ObjectiveParts.RangeOfMotion)}
                      onRemoveRegion={onRemoveRegion(ObjectiveParts.RangeOfMotion)}
                      setVisitForm={setVisitForm}
                      complaintId={complaintId}
                      romSliderStep={romSliderStep}
                      signed={signed || readOnly}
                    />
                  </div>
                )}
                {partOpen === ObjectiveParts.Myofascial && (
                  <Myofascial
                    value={value?.[ObjectiveParts.Myofascial]?.detail}
                    onChange={onChangeDetail(ObjectiveParts.Myofascial)}
                    conditions={conditionsRetrieved}
                    signed={signed || readOnly}
                  />
                )}
                {partOpen === ObjectiveParts.Neurological && (
                  <Neurological
                    value={value?.[ObjectiveParts.Neurological]?.detail || {}}
                    onChange={onChangeDetail(ObjectiveParts.Neurological)}
                    signed={signed || readOnly}
                  />
                )}
                {partOpen === ObjectiveParts.Orthopedic && (
                  <Orthopedic
                    value={value?.[ObjectiveParts.Orthopedic]?.detail}
                    onChange={onChangeDetail(ObjectiveParts.Orthopedic)}
                    evalsThatWeNeed={Object.keys(
                      visitForm?.note?.[complaintId]?.objective?.Orthopedic?.detail || {}
                    )}
                  />
                )}
              </div>
            </div>
          )}
        </div>
        <div>
          <Textarea
            value={localTextareaValue}
            rows={5}
            macros={me.macros}
            disabled={signed || readOnly || isSaving}
            onChange={val => {
              if (signed || readOnly) return;
              handleObjectiveChange(val);
            }}
            label={
              <div className="flex justify-between items-center w-full pb-2">
                <span>Notes</span>
                <Checkbox
                  name="carry-forward-objective"
                  value={
                    complaintId ? !!visitForm?.note?.[complaintId]?.objectiveCarryForward : false
                  }
                  disabled={readOnly || signed}
                  onChange={checked => {
                    if (signed || isSaving || readOnly) return;
                    setVisitForm(prev => {
                      if (!complaintId) return prev;
                      const newVal = {
                        ...prev,
                        note: prev?.note ? prev?.note : {},
                      };
                      const newNote = newVal.note?.[complaintId] || {};
                      newNote.objectiveCarryForward = checked;
                      newVal.note[complaintId] = newNote;

                      return newVal;
                    });
                  }}
                  label="Carry Forward"
                />
              </div>
            }
            className="pb-64"
          />
        </div>
      </div>
      <Modal
        isOpen={rangeOfMotionIsOpen}
        close={() => setRangeOfMotionIsOpen(false)}
        isFullScreen={true}
      >
        <RangeOfMotion
          close={() => setRangeOfMotionIsOpen(false)}
          value={value?.[ObjectiveParts.RangeOfMotion]?.detail}
          onChange={onChangeDetail(ObjectiveParts.RangeOfMotion)}
          onRemoveRegion={onRemoveRegion(ObjectiveParts.RangeOfMotion)}
          setVisitForm={setVisitForm}
          complaintId={complaintId}
          romSliderStep={romSliderStep}
          signed={signed || readOnly}
        />
      </Modal>
      <Modal isOpen={jointsIsOpen} close={() => setJointsIsOpen(false)} isFullScreen={true}>
        <Joints
          value={value?.[ObjectiveParts.Joints]?.detail || {}}
          close={() => setJointsIsOpen(false)}
          onChange={onChangeDetail(ObjectiveParts.Joints)}
          onChangeSpine={onChangeSpineV1}
          onRemoveRegion={onRemoveRegion(ObjectiveParts.Joints)}
          isFetching={isFetching}
          signed={signed || readOnly}
        />
      </Modal>

      <Modal
        isOpen={myofascialIsOpen}
        close={() => setMyofascialIsOpen(false)}
        isFullScreen={true}
        className="print:absolute print:top-0 print:left-0 print:border-none print:shadow-none inline-block align-bottom bg-white dark:bg-dark-gray-900 rounded-lg px-4 pt-5 pb-4 text-left shadow-xl transform transition-shadow sm:my-8 sm:align-middle sm:p-6 print:hidden max-w-xl w-full"
      >
        <Myofascial
          close={() => setMyofascialIsOpen(false)}
          value={value?.[ObjectiveParts.Myofascial]?.detail}
          onChange={onChangeDetail(ObjectiveParts.Myofascial)}
          conditions={conditionsRetrieved}
          signed={signed || readOnly}
        />
      </Modal>
      <Modal
        isOpen={neurologicalIsOpen}
        isFullScreen={true}
        close={() => setNeurologicalIsOpen(false)}
      >
        <Neurological
          close={() => setNeurologicalIsOpen(false)}
          value={value?.[ObjectiveParts.Neurological]?.detail || {}}
          onChange={onChangeDetail(ObjectiveParts.Neurological)}
          signed={signed || readOnly}
        />
      </Modal>
      <Modal isOpen={orthopedicIsOpen} close={() => setOrthopedicIsOpen(false)} isFullScreen={true}>
        <Orthopedic
          close={() => setOrthopedicIsOpen(false)}
          value={value?.[ObjectiveParts.Orthopedic]?.detail}
          onChange={onChangeDetail(ObjectiveParts.Orthopedic)}
          evalsThatWeNeed={Object.keys(
            visitForm?.note?.[complaintId]?.objective?.Orthopedic?.detail || {}
          )}
        />
      </Modal>
    </>
  );
};

export default ObjectiveStep;
